import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import { AudiencePicker } from "../AudiencePicker";

const STATUSES = ["Sourcing", "In discussion", "Coming soon"];
const EMPTY = {
  name: "", rank: "", prioritise: "", theme: "", traction: "", latest_round: "",
  valuation: "", valuation_basis: "", round_date: "", round_stage: "", step_up: "",
  crowding: "", foreign_eligibility: "", key_risk: "", source_url: "",
  status: "Sourcing", published: true, private: false, audience: [],
};

const Field = ({ label, value, onChange, testid, full, placeholder, textarea }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="t-cap text-muted-s">{label}</span>
    {textarea ? (
      <textarea rows={2} className="input-solera mt-1 w-full" placeholder={placeholder || ""} value={value} onChange={onChange} data-testid={testid} />
    ) : (
      <input className="input-solera mt-1 w-full" placeholder={placeholder || ""} value={value} onChange={onChange} data-testid={testid} />
    )}
  </label>
);

export function Pipeline({ onPromoted }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [csv, setCsv] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewers, setViewers] = useState({});
  const [members, setMembers] = useState([]);
  const { user } = useAuth();
  const isOwner = !!user?.is_private_owner;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const load = () => api.get("/admin/pipeline").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/members").then((r) => setMembers((r.data || []).filter((m) => m.role === "member"))).catch(() => {});
  }, []);

  const inviteToCircle = async (name, email) => {
    const { data } = await api.post("/admin/private/invite", { name, email });
    setMembers((ms) => (ms.some((m) => m.user_id === data.user_id) ? ms : [...ms, data]));
    toast.success(`${data.name || data.email} can now be added and was emailed a welcome.`);
    return data;
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    setBusy(true);
    try {
      if (editing) { await api.put(`/admin/pipeline/${editing}`, form); toast.success("Company updated"); }
      else { await api.post("/admin/pipeline", form); toast.success("Company added to the pipeline"); }
      setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const edit = (c) => { setEditing(c.pipeline_id); setForm({ ...EMPTY, ...c }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const importCsv = async () => {
    if (!csv) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", csv);
      const { data } = await api.post("/admin/pipeline/import", fd);
      toast.success(`Imported: ${data.created} added, ${data.updated} updated, ${data.skipped} skipped.`);
      setCsv(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doDelete = async (c) => { await api.delete(`/admin/pipeline/${c.pipeline_id}`); toast.success("Removed"); load(); };
  const doPromote = async (c) => { const { data } = await api.post(`/admin/pipeline/${c.pipeline_id}/promote`); toast.success(`Draft deal created for ${c.name}. ${data.tagged} member${data.tagged === 1 ? "" : "s"} notified. Opening it to add price and minimum.`); load(); onPromoted?.(data.deal_id); };
  const runConfirm = async () => { setBusy(true); try { await confirm.run(); setConfirm(null); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  const loadViewers = async (c) => {
    if (viewers[c.pipeline_id]) { setViewers((v) => ({ ...v, [c.pipeline_id]: undefined })); return; }
    try { const { data } = await api.get(`/admin/pipeline/${c.pipeline_id}/interests`); setViewers((v) => ({ ...v, [c.pipeline_id]: data })); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-pipeline">
      <span className="t-label">Pipeline</span>
      <p className="t-small text-muted-s mt-2 measure">Companies you are targeting to bring to the platform. Published entries appear to members under Pipeline, where they can register early interest.</p>

      <div className="mt-8 max-w-3xl" data-testid="pipeline-form">
        <span className="t-cap text-tint">Member-facing details</span>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Company name" value={form.name} onChange={set("name")} testid="pipeline-name-input" />
          <Field label="Theme" value={form.theme} onChange={set("theme")} testid="pipeline-theme-input" placeholder="e.g. AI inference chips" />
          <Field label="Latest round" value={form.latest_round} onChange={set("latest_round")} testid="pipeline-latest-round-input" placeholder="e.g. Series C, USD 875M" />
          <Field label="Valuation (USD m)" value={form.valuation} onChange={set("valuation")} testid="pipeline-valuation-input" />
          <Field label="Round date" value={form.round_date} onChange={set("round_date")} testid="pipeline-round-date-input" />
          <Field label="Round stage" value={form.round_stage} onChange={set("round_stage")} testid="pipeline-round-stage-input" />
          <Field label="Foreign investor eligibility" value={form.foreign_eligibility} onChange={set("foreign_eligibility")} testid="pipeline-foreign-eligibility-input" full />
          <Field label="Source URL" value={form.source_url} onChange={set("source_url")} testid="pipeline-source-url-input" full placeholder="https://" />
        </div>

        <span className="t-cap text-tint block mt-6">Internal research (not shown to members)</span>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Rank" value={form.rank} onChange={set("rank")} testid="pipeline-rank-input" />
          <Field label="Prioritise?" value={form.prioritise} onChange={set("prioritise")} testid="pipeline-prioritise-input" />
          <Field label="Traction or founder credential" value={form.traction} onChange={set("traction")} testid="pipeline-traction-input" full textarea />
          <Field label="Valuation basis" value={form.valuation_basis} onChange={set("valuation_basis")} testid="pipeline-valuation-basis-input" />
          <Field label="Step-up since prior round" value={form.step_up} onChange={set("step_up")} testid="pipeline-step-up-input" />
          <Field label="Crowding (provisional)" value={form.crowding} onChange={set("crowding")} testid="pipeline-crowding-input" />
          <Field label="Key risk" value={form.key_risk} onChange={set("key_risk")} testid="pipeline-key-risk-input" full textarea />
        </div>

        {isOwner && (
          <div className="border border-[#C69A4E]/40 p-4 space-y-3 mt-6" data-testid="pipeline-private-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.private} onChange={(e) => setForm({ ...form, private: e.target.checked })} data-testid="pipeline-private-checkbox" />
              <span className="t-cap text-tint">Private, invite-only company. Only the members you choose below can see it.</span>
            </label>
            {form.private && (
              <AudiencePicker members={members} value={form.audience || []} onChange={(v) => setForm({ ...form, audience: v })} onInvite={inviteToCircle} testidPrefix="pipeline" />
            )}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <label className="block">
            <span className="t-cap text-muted-s">Status</span>
            <select className="input-solera mt-1 w-full" value={form.status} onChange={set("status")} data-testid="pipeline-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 cursor-pointer h-10">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="pipeline-published-checkbox" />
            <span className="t-cap text-muted-s">Visible to members</span>
          </label>
        </div>

        <div className="mt-4 flex gap-4 items-center md:justify-end">
          {editing && <button className="t-label hover:!text-[#E8E6E1]" onClick={cancel} data-testid="pipeline-cancel">Cancel</button>}
          <button className="btn-primary" disabled={busy} onClick={submit} data-testid="pipeline-submit">{editing ? "Save changes" : "Add company"}</button>
        </div>
      </div>

      <div className="mt-8 max-w-3xl" data-testid="pipeline-csv">
        <span className="t-cap text-muted-s measure block">Bulk upload from your wish-list spreadsheet. Save it as CSV. Recognised columns: Rank, Prioritise?, Name (required), Theme, Traction or founder credential, Latest round, Valuation (USD m), Valuation basis, Round date, Round stage, Step-up since prior round, Crowding (provisional), Foreign investor eligibility, Key risk, Source URL. Optional: Status, Published. Rows update existing companies matched by name.</span>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <input type="file" accept=".csv" onChange={(e) => setCsv(e.target.files[0])} className="t-small text-muted-s" data-testid="pipeline-csv-input" />
          <button className="btn-ghost" disabled={!csv || busy} onClick={importCsv} data-testid="pipeline-csv-import">Import CSV</button>
        </div>
      </div>

      <div className="mt-10 space-y-0 max-w-4xl" data-testid="pipeline-list">
        {rows.map((c) => (
          <div key={c.pipeline_id} className="hairline py-4" data-testid={`pipeline-admin-row-${c.pipeline_id}`}>
            <div className="flex gap-4 items-baseline flex-wrap">
              {c.rank !== "" && c.rank != null && <span className="t-cap text-muted-s">#{c.rank}</span>}
              <span className="t-small text-bone">{c.name}</span>
              {c.private && <span className="t-label !text-[#C69A4E]" data-testid={`pipeline-private-badge-${c.pipeline_id}`}>Private</span>}
              <span className="t-label text-tint">{c.status}</span>
              {!c.published && <span className="t-label text-muted-s">Hidden</span>}
              <span className="t-cap text-muted-s">{[c.theme, c.round_stage].filter(Boolean).join(" · ")}</span>
              {c.valuation && <span className="t-cap fig text-muted-s">{c.valuation}</span>}
              <button onClick={() => loadViewers(c)} className="t-cap text-muted-s hover:!text-[#E8E6E1] ml-auto" data-testid={`pipeline-interest-count-${c.pipeline_id}`}>{c.interest_count} interested</button>
              <button onClick={() => setConfirm({ title: "Promote to deal", message: `Create a draft deal from ${c.name} and notify the ${c.interest_count} member${c.interest_count === 1 ? "" : "s"} who registered interest? It will leave the pipeline and appear as a draft under Deals for you to finish.`, confirmLabel: "Promote", danger: false, run: () => doPromote(c) })} className="t-label hover:!text-[#E8E6E1]" data-testid={`pipeline-promote-${c.pipeline_id}`}>Promote</button>
              <button onClick={() => edit(c)} className="t-label hover:!text-[#E8E6E1]" data-testid={`pipeline-edit-${c.pipeline_id}`}>Edit</button>
              <button onClick={() => setConfirm({ title: "Remove company", message: `Remove ${c.name} from the pipeline? This also clears registered interest.`, confirmLabel: "Remove", danger: true, run: () => doDelete(c) })} className="t-label hover:!text-[#E8E6E1]" data-testid={`pipeline-delete-${c.pipeline_id}`}>Delete</button>
            </div>
            {c.latest_round && <p className="t-cap text-muted-s mt-1">{c.latest_round}{c.round_date ? ` · ${c.round_date}` : ""}</p>}
            {c.traction && <p className="t-cap text-muted-s mt-1 measure">{c.traction}</p>}
            {c.key_risk && <p className="t-cap text-muted-s mt-1 measure"><span className="text-tint">Key risk:</span> {c.key_risk}</p>}
            {viewers[c.pipeline_id] && (
              <div className="mt-2 pl-4" data-testid={`pipeline-viewers-${c.pipeline_id}`}>
                {viewers[c.pipeline_id].length === 0
                  ? <span className="t-cap text-muted-s">No interest yet.</span>
                  : viewers[c.pipeline_id].map((v, i) => <div key={i} className="t-cap text-muted-s">{v.name} · {v.email}</div>)}
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="t-small text-muted-s">No companies yet. Add one above or import a CSV.</p>}
      </div>

      <ConfirmDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }} title={confirm?.title || ""} message={confirm?.message || ""} confirmLabel={confirm?.confirmLabel || "Confirm"} danger={confirm?.danger} busy={busy} onConfirm={runConfirm} />
    </div>
  );
}
